import { useEffect, useState } from "react";

/**
 * SplashScreen — full-bleed Kinng intro shown once per session on app boot.
 * Animates the king mark + tagline with a slate-950 backdrop.
 */
const SplashScreen = ({ onComplete, duration = 1500 }) => {
    const [phase, setPhase] = useState("enter"); // enter -> hold -> exit

    useEffect(() => {
        const t1 = setTimeout(() => setPhase("hold"), 250);
        const t2 = setTimeout(() => setPhase("exit"), duration);
        const t3 = setTimeout(() => onComplete && onComplete(), duration + 450);
        return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
    }, [duration, onComplete]);

    return (
        <div
            data-testid="splash-screen"
            className={`fixed inset-0 z-[200] flex items-center justify-center bg-slate-950 transition-opacity duration-500 ${
                phase === "exit" ? "opacity-0 pointer-events-none" : "opacity-100"
            }`}
        >
            {/* Radial glow */}
            <div
                aria-hidden
                className="absolute inset-0 pointer-events-none"
                style={{
                    background:
                        "radial-gradient(circle at 50% 50%, rgba(212,175,55,0.18) 0%, rgba(212,175,55,0.06) 25%, transparent 60%)",
                }}
            ></div>

            {/* Mark + wordmark */}
            <div className="relative flex flex-col items-center gap-5">
                <div
                    className={`splash-pulse rounded-full transition-all duration-700 ${
                        phase === "enter" ? "scale-90 opacity-0" : "scale-100 opacity-100"
                    }`}
                    style={{
                        boxShadow: "0 0 80px -10px rgba(212,175,55,0.5)",
                    }}
                >
                    <img
                        src="/brand/kinng-mark-transparent.png"
                        alt="Kinng.app"
                        className="h-28 w-28 sm:h-36 sm:w-36 object-contain"
                    />
                </div>
                <div
                    className={`flex flex-col items-center gap-2 transition-all duration-700 delay-200 ${
                        phase === "enter" ? "translate-y-2 opacity-0" : "translate-y-0 opacity-100"
                    }`}
                >
                    <span className="salon-heading text-4xl sm:text-5xl text-white leading-none tracking-wider">
                        Kinng<span className="text-amber-400">.app</span>
                    </span>
                    <span className="text-[10px] sm:text-[11px] uppercase tracking-[0.4em] text-slate-500 font-bold">
                        Reduza no-show em 80%
                    </span>
                </div>

                {/* Hairline loader */}
                <div className="w-40 h-px overflow-hidden bg-slate-800 rounded-full mt-2">
                    <div
                        className="h-full bg-amber-400 splash-bar"
                        style={{ animation: `splash-load ${duration}ms ease-out forwards` }}
                    ></div>
                </div>
            </div>

            <style>{`
                @keyframes splash-load {
                    from { transform: translateX(-100%); }
                    to { transform: translateX(0%); }
                }
                .splash-pulse {
                    animation: splash-pulse 1.6s ease-in-out infinite;
                }
                @keyframes splash-pulse {
                    0%, 100% { transform: scale(1); }
                    50% { transform: scale(1.04); }
                }
            `}</style>
        </div>
    );
};

export default SplashScreen;
